import { useState, useEffect } from 'react';
import { Wifi, WifiOff, Signal, SignalLow, SignalMedium, SignalHigh, Gauge } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  useConnectionQuality, 
  type ConnectionQuality,
  getConnectionInfo,
  subscribeToConnectionQuality 
} from '@/hooks/useConnectionQuality';

interface ConnectionQualityIndicatorProps {
  className?: string;
  showDetails?: boolean;
  variant?: 'badge' | 'icon' | 'full';
}

const qualityConfig: Record<ConnectionQuality, {
  icon: typeof Wifi;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
}> = {
  excellent: {
    icon: SignalHigh,
    label: 'Excellent',
    color: 'text-success',
    bgColor: 'bg-success/10',
    borderColor: 'border-success/30',
    description: 'Fast and stable connection',
  },
  good: {
    icon: SignalMedium,
    label: 'Good',
    color: 'text-success',
    bgColor: 'bg-success/10',
    borderColor: 'border-success/30',
    description: 'Connection is stable',
  },
  slow: {
    icon: SignalLow,
    label: 'Slow',
    color: 'text-warning',
    bgColor: 'bg-warning/10',
    borderColor: 'border-warning/30',
    description: 'Slower than usual. Some features may take longer.',
  },
  'very-slow': {
    icon: Signal,
    label: 'Very Slow',
    color: 'text-destructive',
    bgColor: 'bg-destructive/10',
    borderColor: 'border-destructive/30',
    description: 'Very slow connection. Consider waiting for better connectivity.',
  },
  offline: {
    icon: WifiOff,
    label: 'Offline',
    color: 'text-destructive',
    bgColor: 'bg-destructive/10',
    borderColor: 'border-destructive/30',
    description: 'No internet connection. Some features are unavailable.',
  },
};

export const ConnectionQualityIndicator = ({
  className,
  showDetails = false,
  variant = 'badge',
}: ConnectionQualityIndicatorProps) => {
  const { quality, effectiveType, downlink, rtt, saveData, isOnline } = useConnectionQuality();
  
  const config = qualityConfig[quality];
  const Icon = config.icon;

  // Don't show for excellent/good connections unless explicitly requested
  if ((quality === 'excellent' || quality === 'good') && !showDetails) {
    return null;
  }

  if (variant === 'icon') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('cursor-help', config.color, className)}>
            <Icon className={cn('h-4 w-4', quality === 'offline' && 'animate-pulse')} />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            <p className="font-medium">{config.label} Connection</p>
            <p className="text-muted-foreground text-xs">{config.description}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (variant === 'full') {
    return (
      <div className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg border',
        config.bgColor,
        config.borderColor,
        className
      )}>
        <Icon className={cn('h-5 w-5', config.color, quality === 'offline' && 'animate-pulse')} />
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm font-medium', config.color)}>{config.label}</p>
          <p className="text-xs text-muted-foreground truncate">{config.description}</p>
        </div>
        {showDetails && isOnline && (
          <div className="text-right text-xs text-muted-foreground">
            {effectiveType && <p>{effectiveType.toUpperCase()}</p>}
            {rtt && <p>{rtt}ms</p>}
            {downlink && <p>{downlink.toFixed(1)} Mbps</p>}
          </div>
        )}
      </div>
    );
  }

  // Default: badge variant
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={cn(
            'gap-1.5 cursor-help',
            config.bgColor,
            config.borderColor,
            config.color,
            className
          )}
        >
          <Icon className={cn('h-3 w-3', quality === 'offline' && 'animate-pulse')} />
          <span className="text-xs">{config.label}</span>
          {saveData && (
            <span title="Data Saver enabled">
              <Gauge className="h-3 w-3 ml-0.5" />
            </span>
          )}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-sm space-y-1">
          <p className="font-medium">{config.label} Connection</p>
          <p className="text-muted-foreground text-xs">{config.description}</p>
          {isOnline && (
            <div className="text-xs text-muted-foreground pt-1 border-t border-border mt-1">
              {effectiveType && <p>Type: {effectiveType.toUpperCase()}</p>}
              {rtt !== null && <p>Latency: {rtt}ms</p>}
              {downlink !== null && <p>Speed: {downlink.toFixed(1)} Mbps</p>}
              {saveData && <p className="text-warning">Data Saver enabled</p>}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

// Compact version for header
export const ConnectionQualityBadge = ({ className }: { className?: string }) => {
  const [info, setInfo] = useState(getConnectionInfo());

  useEffect(() => {
    const unsubscribe = subscribeToConnectionQuality(setInfo);
    return unsubscribe;
  }, []);

  // Only show for slow/very-slow/offline
  if (info.quality === 'excellent' || info.quality === 'good') {
    return null;
  }

  const config = qualityConfig[info.quality];
  const Icon = config.icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={cn(
            'gap-1 cursor-help',
            config.bgColor,
            config.borderColor,
            config.color,
            className
          )}
        >
          <Icon className={cn('h-3 w-3', info.quality === 'offline' && 'animate-pulse')} />
          <span className="text-xs hidden sm:inline">{config.label}</span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <div className="text-sm">
          <p className="font-medium">{config.label} Connection</p>
          <p className="text-muted-foreground text-xs max-w-[200px]">{config.description}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
